/**
 * ONE MEANING PER NUMBER IN THE ADS TAB (fix round 2, 2026-09-30).
 *
 * CTR. The house ads table (components/admin/ads-tab.tsx, lane A) divided
 * clicks by VIEWABLE impressions, falling back to served, while Compare
 * (components/admin/ads-compare-panel.tsx, lane E, and `readCompare` in
 * lib/ads/compare.ts) divided clicks by impressions, and both columns were
 * headed "CTR". The industry's click-through rate is clicks over impressions,
 * so both tables now use it, through this one helper, under one label and
 * one tooltip. Neither table shows a viewable rate; a viewable count stays a
 * count ("Viewable", "View"). A rate over no impressions is "n/a", never a
 * made-up 0%.
 *
 * LATENCY. Compare drew a house row's latency as a bare "/", the two empty
 * percentiles either side of the separator. House ads are picked on the
 * platform, with no network call, so there is nothing to time: "n/a", with a
 * tooltip that says why. A network source with no call in the window gets
 * "n/a" too, with its own reason.
 *
 * Admin English like the rest of the tab (a staff surface; nothing here
 * reaches a person who sees an ad), so no en.json keys.
 */

import { canonicalAdSource } from '@/lib/ads/types';

export const CTR_LABEL = 'CTR';
export const CTR_TITLE = 'Click-through rate: clicks over impressions';

/** Clicks over impressions as a percentage, two decimals; "n/a" when nothing was shown. */
export function ctrText(clicks: number, impressions: number): string {
  if (!(impressions > 0) || !Number.isFinite(clicks)) return 'n/a';
  return `${((clicks / impressions) * 100).toFixed(2)}%`;
}

export const HOUSE_LATENCY_TITLE = 'House ads are picked on the platform, with no network call, so there is no latency to measure.';
export const NO_CALLS_LATENCY_TITLE = 'No call to this source in this window, so there is no latency to measure.';

/** What one latency bucket bound reads as: the open top bucket is "over the last bound". */
function ms(v: number): string {
  return v > 1000 ? '>1000' : String(v);
}

/** The Compare table's latency cell: "p50/p95", or "n/a" with the reason. */
export function latencyCell(row: { source: string; p50Ms: number | null; p95Ms: number | null }): {
  text: string;
  title?: string;
} {
  if (row.p50Ms === null || row.p95Ms === null) {
    return {
      text: 'n/a',
      title: canonicalAdSource(row.source) === 'house' ? HOUSE_LATENCY_TITLE : NO_CALLS_LATENCY_TITLE,
    };
  }
  return { text: `${ms(row.p50Ms)}/${ms(row.p95Ms)}` };
}
